import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from './ui/Button';
import { Card } from './ui/Card';
import { Input } from './ui/Input';
import { Label } from './ui/Label';
import { Alert } from './ui/Alert';
import { SkeletonRows } from './ui/Skeleton';
import { StatusBadge } from './ui/StatusBadge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/Select';
import { SheetBody, SheetFooter } from './ui/Sheet';
import { Dialog, DialogContent, DialogFooter } from './ui/Dialog';
import { AutomationTimeline } from './AutomationTimeline';
import { FlowCanvas } from './FlowCanvas';
import {
  AbSplitFields,
  AlertFields,
  EndFields,
  FieldError,
  SplitFields,
  UpdateContactFields,
  WebhookFields,
} from './FlowStepEditors';
import {
  ADDABLE_STEP_TYPES,
  MAX_FLOW_STEPS,
  STEP_TITLES,
  addStep,
  issuesFor,
  moveProblem,
  moveStep,
  removeProblem,
  removeStep,
  retargetsForRemoval,
  stepName,
  validateFlow,
  type FlowIssue,
} from '../lib/flowEdit';
import { apiFetch } from '../lib/api';
import { createDesignMails, missingMailsNote, withDesignMails } from '../lib/designMails';
import { useAuth } from '../lib/auth';
import { describeStep, previewTimeline } from '../lib/timeline';
import { LEGAL_BASIS_LABELS, TRIGGER_LABELS, purposeLabel } from '../lib/automationLabels';
import { formatMoney, formatNumber } from '../lib/format';
import { ApiError } from '../types/api';
import { FLOW_LANGUAGES } from '../types/api';
import type {
  AutomationDetailResponse,
  AutomationSaveResponse,
  FlowDefinition,
  FlowLanguageVariant,
  FlowStepType,
  TemplatesListResponse,
} from '../types/api';

/** Radix `Select` doesn't accept an empty-string item value — this sentinel stands in for "no template" (`templateId: null`). */
const NO_TEMPLATE_VALUE = '__none__';

type FlowStep = FlowDefinition['steps'][number];

type StepsView = 'list' | 'canvas';
const STEPS_VIEW_KEY = 'np-flow.stepsView';

/** The viewer's last choice between the step list and the canvas. Storage can be blocked or empty; the list is the default. */
function readStepsView(): StepsView {
  try {
    return localStorage.getItem(STEPS_VIEW_KEY) === 'canvas' ? 'canvas' : 'list';
  } catch {
    return 'list';
  }
}
function saveStepsView(view: StepsView) {
  try {
    localStorage.setItem(STEPS_VIEW_KEY, view);
  } catch {
    // Not remembered; the choice still holds for this visit.
  }
}

function secondsToUnit(seconds: number): { value: number; unit: 'days' | 'hours' | 'minutes' } {
  if (seconds > 0 && seconds % 86400 === 0) return { value: seconds / 86400, unit: 'days' };
  if (seconds > 0 && seconds % 3600 === 0) return { value: seconds / 3600, unit: 'hours' };
  return { value: Math.round(seconds / 60), unit: 'minutes' };
}
const UNIT_SECONDS: Record<'days' | 'hours' | 'minutes', number> = {
  days: 86400,
  hours: 3600,
  minutes: 60,
};

function WaitFields({
  step,
  onPatch,
}: {
  step: FlowStep;
  onPatch: (patch: Partial<FlowStep>) => void;
}) {
  const seconds = typeof step.seconds === 'number' ? step.seconds : 0;
  const { value, unit } = secondsToUnit(seconds);
  return (
    <div className="flex items-end gap-2.5">
      <div className="flex flex-col gap-1.5">
        <Label htmlFor={`wait-value-${step.id}`}>Wait time</Label>
        <Input
          id={`wait-value-${step.id}`}
          type="number"
          min={0}
          className="w-24"
          value={value}
          onChange={(e) => onPatch({ seconds: Number(e.target.value) * UNIT_SECONDS[unit] })}
        />
      </div>
      <select
        aria-label={`Wait time unit, step ${step.id}`}
        className="h-[42px] rounded-[9px] border border-zinc-300 px-3 text-sm"
        value={unit}
        onChange={(e) => {
          const newUnit = e.target.value as 'days' | 'hours' | 'minutes';
          onPatch({ seconds: value * UNIT_SECONDS[newUnit] });
        }}
      >
        <option value="minutes">minutes</option>
        <option value="hours">hours</option>
        <option value="days">days</option>
      </select>
    </div>
  );
}

function SendTemplateFields({
  step,
  onPatch,
}: {
  step: FlowStep;
  onPatch: (patch: Partial<FlowStep>) => void;
}) {
  const { data } = useQuery({
    queryKey: ['templates'],
    queryFn: () => apiFetch<TemplatesListResponse>('/templates'),
  });
  const templates = data?.templates ?? [];
  const currentValue = typeof step.templateId === 'string' ? step.templateId : NO_TEMPLATE_VALUE;
  const variants = Array.isArray(step.variants) ? (step.variants as FlowLanguageVariant[]) : [];
  const unusedLanguages = FLOW_LANGUAGES.filter(
    (l) => !variants.some((v) => v.language === l.code),
  );

  // An empty list is saved as "no variants" (the key is dropped), so a flow
  // whose languages were all removed is the same as one that never had any.
  const setVariants = (next: FlowLanguageVariant[]) =>
    onPatch({ variants: next.length > 0 ? next : undefined });
  const patchVariant = (language: string, patch: Partial<FlowLanguageVariant>) =>
    setVariants(variants.map((v) => (v.language === language ? { ...v, ...patch } : v)));

  return (
    <div className="flex flex-col gap-3">
      {variants.length > 0 ? (
        <p className="m-0 text-xs font-medium text-zinc-500">All other languages</p>
      ) : null}
      <div className="flex flex-col gap-1.5">
        <Label htmlFor={`subject-${step.id}`}>Subject line</Label>
        <Input
          id={`subject-${step.id}`}
          value={typeof step.subject === 'string' ? step.subject : ''}
          onChange={(e) => onPatch({ subject: e.target.value })}
        />
      </div>
      <div className="flex flex-col gap-1.5">
        <Label htmlFor={`template-${step.id}`}>Template</Label>
        <Select
          value={currentValue}
          onValueChange={(value) =>
            onPatch({ templateId: value === NO_TEMPLATE_VALUE ? null : value })
          }
        >
          <SelectTrigger id={`template-${step.id}`}>
            <SelectValue placeholder="Select template" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={NO_TEMPLATE_VALUE}>No template</SelectItem>
            {templates.map((t) => (
              <SelectItem key={t.id} value={t.id}>
                {t.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {!step.templateId ? (
          <p className="m-0 text-xs text-warning-text">
            Without a template, the flow cannot be activated.
          </p>
        ) : null}
      </div>
      <div className="flex flex-col gap-2.5">
        <p className="m-0 text-sm font-medium">Languages</p>
        {variants.length === 0 ? (
          <p className="m-0 text-xs text-zinc-500">
            Every contact gets this email. Add a language to send its own template and subject to
            contacts who speak it.
          </p>
        ) : null}
        {variants.map((variant) => {
          const name =
            FLOW_LANGUAGES.find((l) => l.code === variant.language)?.name ?? variant.language;
          return (
            <div
              key={variant.language}
              className="flex flex-col gap-2 rounded-[9px] border border-zinc-200 p-3"
            >
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">{name}</span>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  aria-label={`Remove ${name}, step ${step.id}`}
                  onClick={() =>
                    setVariants(variants.filter((v) => v.language !== variant.language))
                  }
                >
                  Remove
                </Button>
              </div>
              <Label htmlFor={`subject-${step.id}-${variant.language}`}>
                Subject line ({name})
              </Label>
              <Input
                id={`subject-${step.id}-${variant.language}`}
                value={variant.subject}
                onChange={(e) => patchVariant(variant.language, { subject: e.target.value })}
              />
              <Label htmlFor={`template-${step.id}-${variant.language}`}>Template ({name})</Label>
              <Select
                value={variant.templateId ?? NO_TEMPLATE_VALUE}
                onValueChange={(value) =>
                  patchVariant(variant.language, {
                    templateId: value === NO_TEMPLATE_VALUE ? null : value,
                  })
                }
              >
                <SelectTrigger id={`template-${step.id}-${variant.language}`}>
                  <SelectValue placeholder="Select template" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NO_TEMPLATE_VALUE}>No template</SelectItem>
                  {templates.map((t) => (
                    <SelectItem key={t.id} value={t.id}>
                      {t.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {!variant.templateId ? (
                <p className="m-0 text-xs text-warning-text">
                  Without a template, the flow cannot be activated.
                </p>
              ) : null}
            </div>
          );
        })}
        {unusedLanguages.length > 0 ? (
          <select
            aria-label={`Add language, step ${step.id}`}
            className="h-[42px] rounded-[9px] border border-zinc-300 px-3 text-sm"
            value=""
            onChange={(e) => {
              if (!e.target.value) return;
              // A new language starts from the default's subject and template, so it is valid right away.
              setVariants([
                ...variants,
                {
                  language: e.target.value,
                  templateId: typeof step.templateId === 'string' ? step.templateId : null,
                  subject: typeof step.subject === 'string' ? step.subject : '',
                },
              ]);
            }}
          >
            <option value="">Add language…</option>
            {unusedLanguages.map((l) => (
              <option key={l.code} value={l.code}>
                {l.name}
              </option>
            ))}
          </select>
        ) : null}
      </div>
      {typeof step.purpose === 'string' ? (
        <div>
          <StatusBadge tone="accent" icon="✉">
            {purposeLabel(step.purpose)}
          </StatusBadge>
        </div>
      ) : null}
    </div>
  );
}

function CouponFields({
  step,
  onPatch,
}: {
  step: FlowStep;
  onPatch: (patch: Partial<FlowStep>) => void;
}) {
  return (
    <div className="flex items-end gap-2.5">
      <div className="flex flex-col gap-1.5">
        {/* Bounds match core's MAX_COUPON_PERCENT / MAX_COUPON_VALID_DAYS (flows/types.ts), which the save rejects beyond. */}
        <Label htmlFor={`percent-${step.id}`}>Discount (%)</Label>
        <Input
          id={`percent-${step.id}`}
          type="number"
          min={1}
          max={50}
          className="w-24"
          value={typeof step.percent === 'number' ? step.percent : 0}
          onChange={(e) => onPatch({ percent: Number(e.target.value) })}
        />
      </div>
      <div className="flex flex-col gap-1.5">
        <Label htmlFor={`validdays-${step.id}`}>Valid for (days)</Label>
        <Input
          id={`validdays-${step.id}`}
          type="number"
          min={1}
          max={30}
          className="w-24"
          value={typeof step.validDays === 'number' ? step.validDays : 0}
          onChange={(e) => onPatch({ validDays: Number(e.target.value) })}
        />
      </div>
      {typeof step.prefix === 'string' ? (
        <span className="text-xs text-muted font-mono self-center">Prefix: {step.prefix}</span>
      ) : null}
    </div>
  );
}

function StepCard({
  step,
  index,
  steps,
  issues,
  onPatch,
  onMove,
  onRemove,
}: {
  step: FlowStep;
  index: number;
  steps: FlowStep[];
  issues: FlowIssue[];
  onPatch: (patch: Partial<FlowStep>) => void;
  onMove: (dir: -1 | 1) => void;
  onRemove: () => void;
}) {
  const def = { steps };
  const n = index + 1;
  const up = moveProblem(def, index, -1);
  const down = moveProblem(def, index, 1);
  const editorProps = { step, index, steps, issues, onPatch };
  const moveButton = (dir: -1 | 1, problem: string | null) => (
    <Button
      type="button"
      variant="ghost"
      size="sm"
      disabled={problem !== null}
      title={problem ?? undefined}
      aria-label={`Move step ${n} ${dir === -1 ? 'up' : 'down'}`}
      onClick={() => onMove(dir)}
    >
      {dir === -1 ? 'Up' : 'Down'}
    </Button>
  );
  return (
    <Card id={`step-card-${step.id}`} tabIndex={-1} className="p-4 flex flex-col gap-3">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="text-xs text-muted font-medium">
          Step {n} · {STEP_TITLES[step.type] ?? step.type}
        </div>
        <div className="flex items-center gap-1">
          {moveButton(-1, up)}
          {moveButton(1, down)}
          <Button
            type="button"
            variant="ghost"
            size="sm"
            aria-label={`Remove step ${n}`}
            onClick={onRemove}
          >
            Remove
          </Button>
        </div>
      </div>
      {up && index > 0 ? <p className="m-0 text-xs text-muted">Cannot move up: {up}</p> : null}
      {down && index < steps.length - 1 ? (
        <p className="m-0 text-xs text-muted">Cannot move down: {down}</p>
      ) : null}
      {step.type === 'wait' ? (
        <>
          <WaitFields step={step} onPatch={onPatch} />
          <FieldError issues={issues} stepId={step.id} field="seconds" />
        </>
      ) : null}
      {step.type === 'send_template' ? (
        <>
          <SendTemplateFields step={step} onPatch={onPatch} />
          <FieldError issues={issues} stepId={step.id} field="subject" />
        </>
      ) : null}
      {step.type === 'create_coupon' ? (
        <>
          <CouponFields step={step} onPatch={onPatch} />
          <FieldError issues={issues} stepId={step.id} field="percent" />
          <FieldError issues={issues} stepId={step.id} field="validDays" />
          <FieldError issues={issues} stepId={step.id} field="prefix" />
        </>
      ) : null}
      {step.type === 'condition' ? (
        <p className="m-0 text-sm text-muted">{describeStep(step)} (cannot be edited here).</p>
      ) : null}
      {step.type === 'update_contact' ? <UpdateContactFields {...editorProps} /> : null}
      {step.type === 'split' ? <SplitFields {...editorProps} /> : null}
      {step.type === 'ab_split' ? <AbSplitFields {...editorProps} /> : null}
      {step.type === 'internal_alert' ? <AlertFields {...editorProps} /> : null}
      {step.type === 'webhook' ? <WebhookFields {...editorProps} /> : null}
      {step.type === 'end' ? <EndFields /> : null}
      {issuesFor(issues, step.id, 'action').length > 0 ? (
        <FieldError issues={issues} stepId={step.id} field="action" />
      ) : null}
    </Card>
  );
}

/** Picks a step type to insert at `position` (0 = first). Native select, reset after each choice. */
function AddStepSelect({
  position,
  disabled,
  onAdd,
}: {
  position: number;
  disabled: boolean;
  onAdd: (type: FlowStepType) => void;
}) {
  const label = position === 0 ? 'Add a step at the start' : `Add a step after step ${position}`;
  return (
    <select
      aria-label={label}
      disabled={disabled}
      title={disabled ? `A flow can have at most ${MAX_FLOW_STEPS} steps.` : undefined}
      className="h-[38px] rounded-[9px] border border-dashed border-zinc-300 bg-transparent px-3 text-sm text-muted"
      value=""
      onChange={(e) => {
        if (e.target.value) onAdd(e.target.value as FlowStepType);
      }}
    >
      <option value="">+ Add a step…</option>
      {ADDABLE_STEP_TYPES.map((t) => (
        <option key={t} value={t}>
          {STEP_TITLES[t]}
        </option>
      ))}
    </select>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-xs text-muted">{label}</div>
      <div className="text-lg font-semibold">{value}</div>
    </div>
  );
}

/**
 * The editable contents of the "Edit flow" side sheet (direction A): stats, trigger, stop
 * rules, legal basis, editable step fields and the live timeline, all stacked and scrolling
 * together in `SheetBody`, with `SheetFooter` pinned below. Used from `AutomatiseringerPage`,
 * mounted only while `/automatiseringer/:id` is the active route.
 */
export function AutomationEditPane({
  id,
  onClose,
  notice = null,
}: {
  id: string;
  onClose: () => void;
  /** A note to show at the top, e.g. that installing could not create every mail. */
  notice?: string | null;
}) {
  const queryClient = useQueryClient();
  const { isAdmin } = useAuth();
  const [form, setForm] = useState<FlowDefinition | null>(null);
  const [stepsView, setStepsView] = useState<StepsView>(readStepsView);
  // A node chosen on the canvas: the list opens and that step's card gets the focus.
  const [focusStepId, setFocusStepId] = useState<string | null>(null);
  // The step a viewer is about to remove (the confirm dialog), and what the last removal re-pointed.
  const [removeId, setRemoveId] = useState<string | null>(null);
  const [editNote, setEditNote] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['automation', id],
    queryFn: () => apiFetch<AutomationDetailResponse>(`/automations/${id}`),
  });

  useEffect(() => {
    if (data?.definition) setForm(data.definition);
  }, [data?.definition]);

  useEffect(() => {
    if (!focusStepId || stepsView !== 'list') return;
    const card = document.getElementById(`step-card-${focusStepId}`);
    if (card) {
      card.scrollIntoView?.({ block: 'center' });
      card.focus();
      setFocusStepId(null);
    }
  }, [focusStepId, stepsView, form]);

  const saveMutation = useMutation({
    mutationFn: (definition: FlowDefinition) =>
      apiFetch<AutomationSaveResponse>(`/automations/${id}`, {
        method: 'PUT',
        body: { definition },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['automation', id] });
      void queryClient.invalidateQueries({ queryKey: ['automations'] });
      onClose();
    },
  });

  // Creates the flow's designed mails as new templates (one request per
  // mail, in every language) and puts them into the steps in the form;
  // nothing is saved until "Save changes".
  const [designProgress, setDesignProgress] = useState<string | null>(null);
  const designMutation = useMutation({
    mutationFn: () =>
      createDesignMails(id, data?.designMails ?? [], (done, total) =>
        setDesignProgress(`${done} of ${total}`),
      ),
    onSuccess: (mails) => {
      void queryClient.invalidateQueries({ queryKey: ['templates'] });
      setForm((f) => (f ? withDesignMails(f, mails) : f));
    },
    onSettled: () => setDesignProgress(null),
  });

  if (isLoading || !data || !form) {
    return (
      <SheetBody>
        <SkeletonRows label="Loading flow…" rows={4} rowClassName="h-24" />
      </SheetBody>
    );
  }

  const { automation, stats } = data;

  function patchStep(stepId: string, patch: Partial<FlowStep>) {
    setForm((f) =>
      f ? { steps: f.steps.map((s) => (s.id === stepId ? { ...s, ...patch } : s)) } : f,
    );
  }

  const liveTimeline = previewTimeline(form);
  const issues = validateFlow(form);

  function insertStep(position: number, type: FlowStepType) {
    const { definition, stepId } = addStep(form as FlowDefinition, type, position);
    setForm(definition);
    setEditNote(null);
    setStepsView('list');
    setFocusStepId(stepId);
  }
  function moveStepBy(index: number, dir: -1 | 1) {
    const stepId = (form as FlowDefinition).steps[index]?.id ?? null;
    setForm((f) => (f ? moveStep(f, index, dir) : f));
    setEditNote(null);
    setFocusStepId(stepId);
  }
  function confirmRemove() {
    const current = form as FlowDefinition;
    const at = current.steps.findIndex((s) => s.id === removeId);
    if (at === -1 || removeId === null) return setRemoveId(null);
    const moved = retargetsForRemoval(current, removeId);
    setEditNote(
      `Removed ${stepName(current.steps, at)}.` +
        (moved.length > 0
          ? ` ${moved.map((m) => `${m.description} now goes to ${m.to}`).join('; ')}.`
          : ''),
    );
    setForm(removeStep(current, removeId));
    setRemoveId(null);
  }
  const removeIndex = removeId ? form.steps.findIndex((s) => s.id === removeId) : -1;
  const removeRetargets = removeId ? retargetsForRemoval(form, removeId) : [];
  const removeBlocked = removeProblem(form);

  return (
    <>
      <SheetBody>
        {notice && !designMutation.isSuccess ? <Alert>{notice}</Alert> : null}
        {saveMutation.isError ? (
          <Alert>
            {saveMutation.error instanceof ApiError
              ? (saveMutation.error.body.message ?? 'Could not save the changes.')
              : 'Could not save the changes.'}
          </Alert>
        ) : null}

        <div className="flex gap-5 flex-wrap">
          <Stat label="Enrolled" value={formatNumber(stats.enrolled)} />
          <Stat label="Active" value={formatNumber(stats.active)} />
          <Stat label="Completed" value={formatNumber(stats.completed)} />
          <Stat label="Sent" value={formatNumber(stats.sent)} />
          {stats.expired > 0 ? (
            <Stat label="Expired, not sent" value={formatNumber(stats.expired)} />
          ) : null}
          {stats.deferredByFrequencyCap > 0 ? (
            <Stat
              label="Delayed by frequency cap"
              value={formatNumber(stats.deferredByFrequencyCap)}
            />
          ) : null}
          {stats.skippedByFrequencyCap > 0 ? (
            <Stat
              label="Skipped by frequency cap"
              value={formatNumber(stats.skippedByFrequencyCap)}
            />
          ) : null}
          <Stat label="Revenue" value={formatMoney(stats.revenue, stats.currency)} />
        </div>

        <section>
          <h2 className="text-sm font-semibold m-0 mb-1">Trigger</h2>
          <p className="text-sm m-0">{TRIGGER_LABELS[automation.triggerType]}</p>
        </section>

        <section aria-labelledby="pane-stop-heading">
          <h2 id="pane-stop-heading" className="text-sm font-semibold m-0 mb-2">
            Stop rules
          </h2>
          <ul className="m-0 pl-5 text-sm flex flex-col gap-1">
            <li>
              {automation.stopOnPurchase
                ? 'Stops if the contact completes a purchase along the way.'
                : 'Continues even if the contact makes a purchase along the way.'}
            </li>
            <li>
              {automation.allowReentry
                ? 'The contact can enroll in the flow again after it has finished.'
                : 'The contact can only complete the flow once.'}
            </li>
          </ul>
        </section>

        <section aria-labelledby="pane-consent-heading">
          <h2 id="pane-consent-heading" className="text-sm font-semibold m-0 mb-2">
            Consent and legal basis
          </h2>
          <ul className="m-0 pl-5 text-sm flex flex-col gap-1">
            {automation.allowedLegalBasis.map((basis) => (
              <li key={basis}>{LEGAL_BASIS_LABELS[basis]}</li>
            ))}
          </ul>
        </section>

        <section aria-labelledby="pane-steps-heading">
          <h2 id="pane-steps-heading" className="text-sm font-semibold m-0 mb-1">
            Steps
          </h2>
          <p className="text-xs text-muted mb-3">
            The order the contact experiences it, top to bottom. Add, move or remove steps here;
            nothing changes for contacts until you save, and a flow that is switched on stays on.
          </p>
          {editNote ? (
            <p role="status" className="mb-3 rounded-xl border border-border px-4 py-3 text-sm">
              {editNote}
            </p>
          ) : null}
          {issues.length > 0 ? (
            <div className="mb-3">
              <Alert>
                <p className="m-0 font-medium">
                  {issues.length === 1 ? '1 problem' : `${issues.length} problems`} to fix before
                  saving:
                </p>
                <ul className="m-0 mt-1 pl-5">
                  {issues.map((i) => {
                    const at = form.steps.findIndex((s) => s.id === i.stepId);
                    return (
                      <li key={`${i.stepId}-${i.field}-${i.message}`}>
                        <button
                          type="button"
                          className="underline text-left"
                          onClick={() => {
                            setStepsView('list');
                            setFocusStepId(i.stepId);
                          }}
                        >
                          {stepName(form.steps, at)}
                        </button>
                        : {i.message}
                      </li>
                    );
                  })}
                </ul>
              </Alert>
            </div>
          ) : null}
          {data.hasDesignTemplates && isAdmin ? (
            <div className="flex flex-col gap-2 mb-3">
              <div className="flex items-center gap-3 flex-wrap">
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={designMutation.isPending}
                  onClick={() => designMutation.mutate()}
                >
                  {designMutation.isPending
                    ? `Creating mails… ${designProgress ?? ''}`.trim()
                    : 'Use the NoPain design mails'}
                </Button>
                <span className="text-xs text-muted">
                  {designMutation.isSuccess
                    ? 'New templates created and chosen below. Save changes to use them.'
                    : 'Creates the designed mails as new templates and picks them for the steps.'}
                </span>
              </div>
              {designMutation.isSuccess && missingMailsNote(designMutation.data.failed) ? (
                <Alert>{missingMailsNote(designMutation.data.failed)}</Alert>
              ) : null}
              {designMutation.isError ? (
                <Alert>
                  {designMutation.error instanceof ApiError
                    ? (designMutation.error.body.message ?? 'Could not create the mails.')
                    : 'Could not create the mails.'}
                </Alert>
              ) : null}
            </div>
          ) : null}
          <div role="group" aria-label="Steps view" className="flex gap-2 mb-3">
            {(['list', 'canvas'] as const).map((view) => (
              <Button
                key={view}
                type="button"
                size="sm"
                variant={stepsView === view ? 'primary' : 'secondary'}
                aria-pressed={stepsView === view}
                onClick={() => {
                  setStepsView(view);
                  saveStepsView(view);
                }}
              >
                {view === 'list' ? 'List' : 'Canvas'}
              </Button>
            ))}
          </div>
          {stepsView === 'canvas' ? (
            <FlowCanvas
              definition={form}
              triggerType={automation.triggerType}
              stats={stats}
              stepStats={data.stepStats}
              onSelectStep={(stepId) => {
                setStepsView('list');
                setFocusStepId(stepId);
              }}
            />
          ) : (
            <div className="flex flex-col gap-3">
              <AddStepSelect
                position={0}
                disabled={form.steps.length >= MAX_FLOW_STEPS}
                onAdd={(type) => insertStep(0, type)}
              />
              {form.steps.map((step, i) => (
                <div key={step.id} className="flex flex-col gap-3">
                  <StepCard
                    step={step}
                    index={i}
                    steps={form.steps}
                    issues={issues}
                    onPatch={(patch) => patchStep(step.id, patch)}
                    onMove={(dir) => moveStepBy(i, dir)}
                    onRemove={() => setRemoveId(step.id)}
                  />
                  <AddStepSelect
                    position={i + 1}
                    disabled={form.steps.length >= MAX_FLOW_STEPS}
                    onAdd={(type) => insertStep(i + 1, type)}
                  />
                </div>
              ))}
            </div>
          )}
        </section>

        <section aria-labelledby="pane-timeline-heading">
          <h2 id="pane-timeline-heading" className="text-sm font-semibold m-0 mb-3">
            How the contact experiences it
          </h2>
          <AutomationTimeline entries={liveTimeline} />
        </section>
      </SheetBody>
      <SheetFooter>
        <Button variant="secondary" className="flex-grow" onClick={onClose}>
          Cancel
        </Button>
        <Button
          className="flex-grow"
          disabled={saveMutation.isPending || issues.length > 0}
          title={issues.length > 0 ? 'Fix the problems in the steps first.' : undefined}
          onClick={() => saveMutation.mutate(form)}
        >
          {saveMutation.isPending ? 'Saving…' : 'Save changes'}
        </Button>
      </SheetFooter>
      <Dialog open={removeId !== null} onOpenChange={(open) => !open && setRemoveId(null)}>
        <DialogContent
          title={removeIndex >= 0 ? `Remove ${stepName(form.steps, removeIndex)}?` : 'Remove step?'}
          description="The step is removed from this draft. Nothing is saved until you save the flow."
        >
          {removeBlocked ? <p className="m-0 mb-3 text-sm">{removeBlocked}</p> : null}
          {removeRetargets.length > 0 ? (
            <div className="mb-3 text-sm">
              <p className="m-0 mb-1">
                Branches that pointed at it will go on to the step after it (numbered as the flow
                will be after removal):
              </p>
              <ul className="m-0 pl-5">
                {removeRetargets.map((r) => (
                  <li key={r.description}>
                    {r.description} → {r.to}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
          <DialogFooter>
            <Button variant="secondary" onClick={() => setRemoveId(null)}>
              Keep step
            </Button>
            <Button disabled={removeBlocked !== null} onClick={confirmRemove}>
              Remove step
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
